import { defineStore } from 'pinia'

interface DialogMessage {
  id: string;
  dialog_id: number;
  role: string;
  content: any[];
  timestamp?: number;
  created_at: number;
  stop_reason?: string;
  feedback?: {
    id: number;
    dialog_id: number;
    message_id: string;
    rate: number;
    comment?: string;
    created_at: string;
    updated_at: string;
  };
}

interface DialogMessagesState {
  messages: { [dialog_id: string]: DialogMessage[] };
}

// Ordering key in milliseconds. `timestamp` is the source of truth (it disambiguates
// a seeded tool_call from the user message it was posted with, which share a
// whole-second `created_at`), but it arrives in two units: dialog history comes back in
// ms (raw ClickHouse) while live `AiMessageCreated` broadcasts cast it down to seconds.
// Normalise both to ms so live and historical messages sort consistently; fall back to
// `created_at` (seconds) when `timestamp` is absent. (ENG-2698)
function messageSortKeyMs(msg: DialogMessage): number {
  const ts = msg.timestamp;
  if (typeof ts === 'number' && ts > 0) {
    return ts < 1e12 ? ts * 1000 : ts;
  }
  return (msg.created_at || 0) * 1000;
}

export const useDialogMessagesStore = defineStore('dialogMessages', {
  state: (): DialogMessagesState => ({
    messages: {}
  }),

  getters: {
    getDialogMessages: (state) => (dialogId: string | number): DialogMessage[] => {
      const id = typeof dialogId === 'number' ? dialogId.toString() : dialogId;
      return state.messages[id] || [];
    }
  },

  actions: {
    addMessage(dialogId: string | number, message: DialogMessage): void {
      const id = typeof dialogId === 'number' ? dialogId.toString() : dialogId;

      if (!this.messages[id]) {
        this.messages[id] = [];
      }

      const existingIndex = this.messages[id].findIndex(msg => msg.id === message.id);

      if (existingIndex >= 0) {
        this.messages[id][existingIndex] = message;
      } else {
        this.messages[id].push(message);
      }

      this.messages[id].sort((a, b) => messageSortKeyMs(a) - messageSortKeyMs(b));
    },

    addMessages(dialogId: string | number, messages: DialogMessage[]): void {
      if (!messages || messages.length === 0) return;

      for (const message of messages) {
        this.addMessage(dialogId, message);
      }
    },

    setDialogMessages(dialogId: string | number, messages: DialogMessage[]): void {
      const id = typeof dialogId === 'number' ? dialogId.toString() : dialogId;

      const sortedMessages = [...messages].sort((a, b) => messageSortKeyMs(a) - messageSortKeyMs(b));
      this.messages[id] = sortedMessages;
    },

    updateMessage(dialogId: string | number, updatedMessage: DialogMessage): void {
      const id = typeof dialogId === 'number' ? dialogId.toString() : dialogId;
      
      if (!this.messages[id]) {
        return;
      }

      const messageIndex = this.messages[id].findIndex(msg => msg.id === updatedMessage.id);
      if (messageIndex >= 0) {
        this.messages[id][messageIndex] = updatedMessage;
      }
    },

    clearAll(): void {
      this.messages = {};
    }
  }
})
